<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Invite to Group</div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert :closeable="false">
                Inviting user <strong class="font-bold">{{ model.email }}</strong> to group
            </vx-alert>
            <div class="flex flex-col gap-8">
                <team-user-group-assignment :team-id="teamId" v-model="assignedGroups" ref="groupAssignmentRef" />
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    :loading="submitting"
                    :disabled="!assignedGroups.length"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Add
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxModal } from '@voxie/frontend-components';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import TeamUserGroupAssignment from './TeamUserGroupAssignment.vue';
import { groupInvitationsClient } from '~/services';

const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['invitedToGroup']);

const submitting = ref(false);
const groupAssignmentRef = ref(null);
const assignedGroups = ref([]);

const updateVisible = (isVisible) => {
    assignedGroups.value = [];
    groupAssignmentRef.value?.reset();
    visible.value = isVisible;
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        for (const group of assignedGroups.value) {
            try {
                await groupInvitationsClient.sendInvitation(props.teamId, group.group_id, {
                    email: model.value.email,
                    role: group.role,
                });
            } catch (groupError) {
                const message = `Failed to invite user to group ${group.group_id} - ${group.name ? ` (${group.name})` : ''}`;
                toasted.error(`${message}: ${catchRequestError(groupError)}`);
            }
        }

        emit('invitedToGroup', model.value);

        toasted.success(`User invited to group`);

        updateVisible(false);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
